import React, { useState } from "react";
import { Ban, ArrowRightLeft } from "lucide-react";
import { TablePaymentRow } from "../types/invoice-table.types";
import { PaymentNoteDialog } from "./payment-note-dialog.component";
import { cn } from "@/dentlab/src/lib/utils";

export interface PaymentContextMenuProps {
	x: number;
	y: number;
	visible: boolean;
	payment: TablePaymentRow;
	onCancelPayment: (allocationId: number, invoiceId: number, note: string) => void;
	onCancelLossAllocation: (allocationId: number, invoiceId: number, note: string) => void;
	onRebookPaymentAsAkonto: (allocationId: number, invoiceId: number, note: string) => void;
	onClose?: () => void;
	readOnly?: boolean;
}

export const PaymentContextMenu: React.FC<PaymentContextMenuProps> = ({
	x,
	y,
	visible,
	payment,
	onCancelPayment,
	onCancelLossAllocation,
	onRebookPaymentAsAkonto,
	onClose,
	readOnly = false,
}) => {
	const [noteDialogOpen, setNoteDialogOpen] = useState(false);
	const [dialogActionType, setDialogActionType] = useState<'cancel' | 'rebook'>('cancel');
	const [isProcessing, setIsProcessing] = useState(false);

	if (!visible) return null;

	// Adjust position to prevent menu from going off-screen
	const menuWidth = 200;
	const menuHeight = 40;
	const adjustedX = x + menuWidth > window.innerWidth ? x - menuWidth : x;
	const adjustedY = y + menuHeight > window.innerHeight ? y - menuHeight : y;

	const handleCancelClick = () => {
		// Check if action is allowed - just return silently if disabled
		if (isDisabled) {
			return;
		}

		if (payment.allocation_id && payment.invoice_id) {
			// Check if this payment was rebooked from another invoice
			const wasRebooked = payment.allocation_note?.includes("(umgebucht von Rechnung");

			if (wasRebooked) {
				const confirmed = window.confirm(
					"Diese Zahlung wurde von einer anderen Rechnung umgebucht. Möchten Sie sie wirklich stornieren?"
				);
				if (!confirmed) {
					onClose?.();
					return;
				}
			}

			// Open note dialog
			setDialogActionType('cancel');
			setNoteDialogOpen(true);
		}
	};

	const handleRebookClick = () => {
		// Check if action is allowed - just return silently if disabled
		if (isDisabled) {
			return;
		}

		if (payment.allocation_id && payment.invoice_id) {
			// Check if this payment was rebooked from another invoice
			const wasRebooked = payment.allocation_note?.includes("(umgebucht von Rechnung");

			if (wasRebooked) {
				const confirmed = window.confirm(
					"Diese Zahlung wurde bereits von einer anderen Rechnung umgebucht. Möchten Sie sie erneut umbuchen?"
				);
				if (!confirmed) {
					onClose?.();
					return;
				}
			}

			// Open note dialog
			setDialogActionType('rebook');
			setNoteDialogOpen(true);
		}
	};

	const handleNoteConfirm = async (note: string) => {
		if (!payment.allocation_id || !payment.invoice_id) return;

		setIsProcessing(true);
		try {
			if (dialogActionType === 'cancel') {
				const allocationType = payment.allocation_type;
				const isLoss = payment.payment_id === null;

				if (isLoss) {
					// Loss allocations have no payment_id
					onCancelLossAllocation(payment.allocation_id, payment.invoice_id, note);
				} else if (allocationType === 2) {
					// Type 2 (REALLOCATED) allocations: Always rebook as akonto
					onRebookPaymentAsAkonto(payment.allocation_id, payment.invoice_id, note);
				} else {
					// Regular payment cancellation
					onCancelPayment(payment.allocation_id, payment.invoice_id, note);
				}
			} else {
				onRebookPaymentAsAkonto(payment.allocation_id, payment.invoice_id, note);
			}
			setNoteDialogOpen(false);
			onClose?.();
		} finally {
			setIsProcessing(false);
		}
	};

	const handleNoteDialogClose = () => {
		if (!isProcessing) {
			setNoteDialogOpen(false);
		}
	};

	// Check if payment is already canceled or rebooked
	const isPaymentCanceled = !!payment.payment_canceled_at;
	const isAllocationCanceled = !!payment.allocation_canceled_at;
	const isCanceled = isPaymentCanceled || isAllocationCanceled;

	// Loss allocations (payment_id === null)
	const isLoss = payment.payment_id === null;

	// Disable actions for: read-only mode or already canceled payments
	const isDisabled = readOnly || isCanceled;

	// Determine menu items and text based on allocation type
	const allocationType = payment.allocation_type;
	const isType1 = allocationType === 1; // DEFAULT
	const isType2 = allocationType === 2; // REALLOCATED
	const isType3 = allocationType === 3; // PSEUDO_ALLOCATION

	const isNegativePayment = (payment.payment_value ?? 0) < 0;
	const showRebookButton = isType1 && !isNegativePayment && !isLoss;
	const cancelButtonText = isLoss ? "Verlust stornieren" : isType2 ? "Akontozuweisung stornieren" : isType3 ? "Akontozahlung stornieren" : "Zahlung stornieren";

	return (
		<>
			<div
				className="fixed z-50 min-w-[12rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
				style={{
					left: Math.max(0, adjustedX),
					top: Math.max(0, adjustedY),
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div className="p-1">
					<button
						className={cn(
							"relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
							isDisabled && "opacity-50"
						)}
						onClick={handleCancelClick}
						title={isCanceled ? "Zahlung wurde bereits storniert" : ""}
						disabled={isDisabled}
					>
						<Ban className="mr-2 h-4 w-4" />
						<span>{cancelButtonText}</span>
					</button>
					{showRebookButton && (
						<button
							className={cn(
								"relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
								isDisabled && "opacity-50"
							)}
							onClick={handleRebookClick}
							title={isCanceled ? "Zahlung wurde bereits storniert" : isLoss ? "Verlust kann nicht umgebucht werden" : ""}
						>
							<ArrowRightLeft className="mr-2 h-4 w-4" />
							<span>Zu Akonto umbuchen</span>
						</button>
					)}
				</div>
			</div>
			<PaymentNoteDialog
				isOpen={noteDialogOpen}
				onClose={handleNoteDialogClose}
				onConfirm={handleNoteConfirm}
				actionType={dialogActionType}
				payment={payment}
				loading={isProcessing}
			/>
		</>
	);
};